import { useEffect, useState, useRef } from 'react';
import { sendAiChat, type ChatMessage } from '../utils/aiClient';
import { loadAISettings } from '../settings/aiSettings';
import { speakPhrase, stopSpeaking, stopChat } from '../utils/tts';

// End of imports

interface Props {
    onClose: () => void;
    onOpenSettings: () => void;
    currentVerses: { num: string; text: string }[];
    currentRef: string; // e.g. "Genesis 1"
    onNavigate: (bookId: string, chapter: number, verse: number) => void;
    messages: ChatMessage[];
    onMessagesChange: (msgs: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void;
    studyMode: boolean;
    onToggleStudyMode: () => void;
    corpusName: string;
    isPublicDomain?: boolean;
    contextText?: string;
    chapterTitle?: string;
    pageLabel?: string; // e.g. "p. 15" or "15%"
    bookTitle?: string;
    author?: string;
}

// ... removed local sanitizeForSpeech ...

// Web Speech API for STT
// @ts-ignore
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

export const ChatDrawer = ({
    onClose, onOpenSettings, currentVerses, currentRef, onNavigate,
    messages, onMessagesChange, studyMode, onToggleStudyMode, corpusName,
    isPublicDomain, contextText, chapterTitle, pageLabel, bookTitle, author
}: Props) => {
    // Lifted state wrapper: Pass ANY arg (function or array) directly to parent
    const setMessages = (arg: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => {
        onMessagesChange(arg);
    };

    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isGeneratingImage, setIsGeneratingImage] = useState(false);
    const [includeContext, setIncludeContext] = useState(true);
    const [expandedImage, setExpandedImage] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    // Image Modal State
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [imagePrompt, setImagePrompt] = useState('');
    const [usageStats, setUsageStats] = useState<any>(null);
    const [chatStats, setChatStats] = useState<any>(null);
    const [isCoaching, setIsCoaching] = useState(false);

    // Track last spoken message to prevent repeats on mount or weird renders
    const lastSpokenRef = useRef<number>(-1);

    // Audio & Style State (Persisted in localStorage ideally, but component state + initial load for now)
    const [audioEnabled, setAudioEnabled] = useState(() => localStorage.getItem('chat_audio_enabled') !== 'false'); // Default ON
    const [replyStyle, setReplyStyle] = useState<'concise' | 'normal' | 'detailed'>(
        (localStorage.getItem('chat_reply_style') as any) || 'concise'
    );
    // Author Voice
    const [authorVoice, setAuthorVoice] = useState(false);
    useEffect(() => {
        const s = loadAISettings();
        if (isPublicDomain && s.authorVoiceDefault) setAuthorVoice(true);
    }, [isPublicDomain]);

    // STT State
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef<any>(null);

    useEffect(() => {
        localStorage.setItem('chat_audio_enabled', String(audioEnabled));
    }, [audioEnabled]);

    useEffect(() => {
        localStorage.setItem('chat_reply_style', replyStyle);
    }, [replyStyle]);

    // Clear validation error when user types
    useEffect(() => {
        if (validationError && input.trim().length > 0) {
            setValidationError(null);
        }
    }, [input, validationError]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }

        // Auto-speak logic
        if (audioEnabled && messages.length > 0) {
            const lastIdx = messages.length - 1;
            const lastMsg = messages[lastIdx];
            // Check if this message is flagged to NOT READ (images)
            const shouldSkip = (lastMsg as any).doNotRead || (lastMsg as any).isImagePlaceholder;

            if (lastMsg.role === 'assistant' && lastSpokenRef.current !== lastIdx && !isLoading && !shouldSkip) {
                if (!isListening) {
                    lastSpokenRef.current = lastIdx;
                    stopChat(); // Stop previous chat utterance
                    speakPhrase(localSanitizeForSpeech(lastMsg.content));
                }
            }
        }
    }, [messages, audioEnabled, isListening, isLoading]);

    // Handle ESC key to close modal
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setExpandedImage(null);
        };
        window.addEventListener('keydown', handleEsc);
        return () => {
            stopChat(); // Stop chat audio on close/unmount
        };
    }, []);

    // Usage Poll
    useEffect(() => {
        // Poll Usage stats on mount
        const loadStats = async () => {
            // Image Caps
            try {
                const r = await fetchApi('/ai/image/usage');
                if (r.ok) setUsageStats(await r.json());
            } catch (e) { }

            // Chat Caps
            try {
                const r = await fetchApi('/ai/chat/usage');
                if (r.ok) setChatStats(await r.json());
            } catch (e) { }
        };
        loadStats();
    }, []);

    const toggleListening = () => {
        if (!SpeechRecognition) return;

        if (isListening) {
            recognitionRef.current?.stop();
            setIsListening(false);
            return;
        }

        stopSpeaking();

        const recog = new SpeechRecognition();
        recog.continuous = false;
        recog.interimResults = false;
        recog.lang = 'en-US';

        recog.onstart = () => setIsListening(true);
        recog.onend = () => setIsListening(false);
        recog.onerror = (e: any) => {
            console.error('STT Error', e);
            setIsListening(false);
        };
        recog.onresult = (e: any) => {
            const transcript = e.results[0][0].transcript;
            setInput(prev => (prev + ' ' + transcript).trim());
        };

        recognitionRef.current = recog;
        recog.start();
    };

    const openImageModal = async () => {
        // Pre-fill prompt logic
        let initialPrompt = input.trim();
        if (!initialPrompt && includeContext) {
            if (bookTitle) initialPrompt = `Illustration for ${bookTitle} ${chapterTitle}`;
            else initialPrompt = "Scene from current page";
        }
        setImagePrompt(initialPrompt);

        // Fetch Usage
        try {
            const res = await fetchApi('/ai/image/usage');
            if (res.ok) {
                const d = await res.json();
                setUsageStats(d);
            }
        } catch (e) { console.error(e); }

        setImageModalOpen(true);
    };

    const handleCoach = async () => {
        setIsCoaching(true);
        try {
            const contextStr = `${bookTitle} ${chapterTitle} - ${contextText?.slice(0, 500)}`;
            const res = await fetchApi('/ai/coach', {
                method: 'POST',
                body: { prompt: imagePrompt, context: contextStr }
            });
            const d = await res.json();
            if (d.ok && d.coachedPrompt) {
                setImagePrompt(d.coachedPrompt);
            }
        } catch (e) { console.error(e); }
        setIsCoaching(false);
    };

    const executeImageGeneration = async (finalPrompt: string) => {
        setImageModalOpen(false); // Close modal

        const pin = localStorage.getItem('fc_tester_pin');
        if (!pin) return;

        // Validation done in modal
        setIsGeneratingImage(true);
        abortControllerRef.current = new AbortController();

        // Stable Placeholder
        const placeholderId = `imgreq_${Date.now()}`;
        setMessages((prev: ChatMessage[]) => [...prev, {
            role: 'assistant',
            content: 'Generating image...',
            isImagePlaceholder: true,
            id: placeholderId
        } as any]);

        try {
            const url = `/api/ai/image`;

            // ... (rest is same, just using finalPrompt)
            const payload = {
                pin,
                userNudge: finalPrompt, // legacy field name for logging
                prompt: finalPrompt,
                size: "1024x1792", // Portrait 9:16 per req? Wait, user req said 1024x1792 in worker code, but earlier I saw 1792x1024 landscape.
                // Worker code I just wrote uses 1024x1792 (Portrait).
                // Let's stick to Portrait for now as it fits phone screens better.
                quality: "standard",
                response_format: "b64_json",
                n: 1
            };

            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                signal: abortControllerRef.current.signal
            });

            const text = await res.text();
            let data: any = {};
            try { data = JSON.parse(text); } catch { }

            // Diagnostics
            // @ts-ignore
            window.__LAST_IMG_EVENT__ = {
                status: res.status,
                hasData: !!data.data,
                b64Len: data.data?.[0]?.b64_json?.length,
                error: data.error
            };

            if (!res.ok || (data.error && !data.data)) {
                // Extract detailed error
                const details = data.details || {};
                const detailsData = details.data || {};
                const nestedError = detailsData.error || {};

                let niceMessage = nestedError.message || detailsData.message || data.error;
                if (!niceMessage) niceMessage = `Image failed (${res.status})`;
                if (nestedError.code) niceMessage += ` (Code: ${nestedError.code})`;

                if (nestedError.code === 'content_policy_violation' || niceMessage.includes('policy')) {
                    niceMessage = `This prompt triggered safety filters. Try a more symbolic description.`;
                }

                setMessages((prev: ChatMessage[]) => prev.map(m => {
                    if ((m as any).id === placeholderId) {
                        return { role: 'assistant', content: niceMessage, isError: true };
                    }
                    return m;
                }));
                return;
            }
            // Parse Answer
            let finalUrl = "";
            const openAiItem = data.data && data.data[0];
            if (openAiItem && openAiItem.b64_json) {
                finalUrl = `data:image/png;base64,${openAiItem.b64_json}`;
            } else if (openAiItem && openAiItem.url) {
                finalUrl = openAiItem.url;
            }

            if (!finalUrl) throw new Error("No sensitive image data returned.");

            // Update placeholder
            setMessages((prev: ChatMessage[]) => prev.map(m => {
                if ((m as any).id === placeholderId) {
                    return {
                        role: 'assistant',
                        content: `Illustration: ${finalPrompt.slice(0, 50)}...`,
                        imageUrl: finalUrl,
                        doNotRead: true
                    };
                }
                return m;
            }));

            // Deduct usage locally (optimistic)
            if (usageStats) {
                setUsageStats({ ...usageStats, dailyUsed: usageStats.dailyUsed + 1 });
            }

        } catch (e: any) {
            if (e.name === 'AbortError') {
                setMessages((prev: ChatMessage[]) => prev.filter(m => (m as any).id !== placeholderId));
            } else {
                setMessages((prev: ChatMessage[]) => prev.map(m => {
                    if ((m as any).id === placeholderId) {
                        return { role: 'assistant', content: `Error: ${e.message}` };
                    }
                    return m;
                }));
            }
        } finally {
            setIsGeneratingImage(false);
            abortControllerRef.current = null;
        }
    };

    // LEGACY / RETRY HANDLER (Not used by new UI flow directly, except maybe retry)
    const handleGenerateImage = () => openImageModal();

    /* 
       OLD IMPLEMENTATION REMOVED TO SAVE SPACE AND AVOID CONFLICT 
       We replaced logic with executeImageGeneration 
    */

    // Retry Wrapper (only used by error bubbles if we implemented that UI, but requirement says "Retry button")
    // We can reuse handleGenerateImage but logic above assumes new request. 
    // For now, if failed, user just clicks Generate again. 
    // Wait, prompt says: "If it fails, replace placeholder with compact error bubble and a Retry button."
    // We will just let them click the main button again for simplicity unless we attach a specific retry handler to the message.

    // Actually, let's keep it simple: The error turns into text. The user can just click "Generate Image" again.
    // The requirement "Retry button" implies it's IN the bubble.
    // We'll address that by making the error message have a callback? Or just keeping the UI simple.
    // "replace the placeholder with a compact error bubble" -> Text message is compact enough.
    // "and a Retry button" -> We can render a button if we detect the text starts with "Image failed" or similar, inside renderContent??
    // Let's stick to standard text updates first.

    const handleCancelImage = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            setIsGeneratingImage(false);
        }
    };


    const handleSend = async () => {
        if (!input.trim() || isLoading) return;
        setValidationError(null);

        // Client-side Cap Check (Optimistic)
        if (chatStats && chatStats.dailyUsed >= chatStats.dailyLimit) {
            setMessages((prev: ChatMessage[]) => [...prev, { role: 'assistant', content: "⚠️ Daily chat limit reached.", isError: true }]);
            return;
        }

        const settings = loadAISettings();
        if (!settings.enableOnline) {
            setMessages((prev: ChatMessage[]) => [...prev, { role: 'user', content: input }, { role: 'assistant', content: "Please enable online features in settings to use chat." }]);
            setInput('');
            return;
        }

        const userMsg: ChatMessage = { role: 'user', content: input };
        setMessages((prev: ChatMessage[]) => [...prev, userMsg]);
        setInput('');
        setIsLoading(true);

        stopSpeaking(); // Quiet when sending

        // Sanitize messages to remove images from history context (save tokens, avoid format errors)
        const cleanHistory = messages.map(({ role, content }) => ({ role, content }));

        // Prepare Context (Packet)
        const activeContextText = contextText || (includeContext ? currentVerses.map(v => v.text).join('\n\n') : undefined);
        const activeContextMeta = {
            title: bookTitle || currentRef,
            author,
            bookId: currentRef.split(' ')[0],
            chapter: chapterTitle || currentRef.split(' ').pop(),
            pageLabel,
            isPublicDomain,
            corpusName
        };

        const res = await sendAiChat({
            messages: [...cleanHistory, userMsg],
            contextText: includeContext ? activeContextText : undefined,
            contextMeta: includeContext ? activeContextMeta : undefined,
            ref: currentRef,
            style: replyStyle,
            personaAddon: settings.personaText,
            studyMode,
            corpusName,
            authorVoice: isPublicDomain ? authorVoice : false,
            explainPage: input.toLowerCase().includes("explain this page") || includeContext
        });

        setIsLoading(false);

        if (res.ok && res.answer) {
            setMessages((prev: ChatMessage[]) => [...prev, { role: 'assistant', content: res.answer! }]);
            // Optimistic stat update
            if (chatStats) setChatStats({ ...chatStats, dailyUsed: chatStats.dailyUsed + 1 });
        } else {
            // Handle structured error
            let msg = `Error: ${res.error}`;
            try {
                // The 'error' field is usually string, but if we structured return?
                // aiClient returns { ok, answer, error }
                // If worker returns { code: 'chat_limit', message: ... }
                // aiClient might pass it through or stringify stat.
                // Just display the error string for now.
            } catch (e) { }

            setMessages((prev: ChatMessage[]) => [...prev, { role: 'assistant', content: msg }]);
        }
    };

    // Helper to render markdown-ish links (naive regex for MVP)
    const parseReadLink = (href: string) => {
        try {
            const prefix = '/read/';
            if (!href.startsWith(prefix)) return null;
            const parts = href.substring(prefix.length).split('?');
            const pathParts = parts[0].split('/');
            const bookId = pathParts[0];
            const chapter = parseInt(pathParts[1], 10);

            const urlParams = new URLSearchParams(parts[1]);
            const verse = parseInt(urlParams.get('verse') || '0', 10);

            if (bookId && !isNaN(chapter)) {
                return { bookId, chapter, verse };
            }
        } catch (e) { console.error(e); }
        return null;
    };

    // New: Sanitize for Speech
    const localSanitizeForSpeech = (text: string) => {
        // Remove Citations section
        let clean = text.split(/Citations:\s*\n/i)[0];
        // Remove markdown links
        clean = clean.replace(/\[([^\]]+)\]\(\/read\/[^)]+\)/g, '$1');
        clean = clean.replace(/\/read\/[a-z0-9-]+\/\d+(\?verse=\d+)?/g, '');
        return clean;
    };

    // Helper to separate Prose vs Citations
    const splitContent = (text: string) => {
        const parts = text.split(/Citations:\s*\n/i);
        const prose = parts[0];
        const citationBlock = parts.length > 1 ? parts[1] : '';
        return { prose, citationBlock };
    };

    const renderProse = (text: string) => {
        const parts = text.split(/(\[[^\]]+\]\([^)]+\))/g);
        return parts.map((part, i) => {
            const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
            if (linkMatch) {
                const label = linkMatch[1];
                const href = linkMatch[2];
                if (href.startsWith('/read/')) {
                    return (
                        <span key={i} style={{ color: '#4CAF50', cursor: 'pointer', textDecoration: 'underline', fontWeight: 500 }}
                            onClick={(e) => {
                                e.preventDefault();
                                const p = parseReadLink(href);
                                if (p) onNavigate(p.bookId, p.chapter, p.verse);
                            }}>
                            {label}
                        </span>
                    );
                }
                return <a key={i} href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#646cff' }}>{label}</a>;
            }

            const boldParts = part.split(/(\*\*[^*]+\*\*)/g);
            return (
                <span key={i}>
                    {boldParts.map((subPart, j) => {
                        const boldMatch = subPart.match(/^\*\*(.*)\*\*$/);
                        if (boldMatch) return <strong key={j}>{boldMatch[1]}</strong>;
                        return subPart;
                    })}
                </span>
            );
        });
    };

    const renderCitations = (block: string) => {
        if (!block || !block.trim()) return null;
        const lines = block.split('\n').map(l => l.trim()).filter(l => l.startsWith('-'));

        return (
            <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {lines.map((line, i) => {
                    const match = line.match(/\[([^\]]+)\]\(([^)]+)\)/);
                    if (match) {
                        const display = match[1];
                        const href = match[2];
                        return (
                            <button key={i}
                                onClick={() => {
                                    const p = parseReadLink(href);
                                    if (p) onNavigate(p.bookId, p.chapter, p.verse);
                                }}
                                style={{
                                    background: '#222', border: '1px solid #4CAF50', color: '#4CAF50',
                                    borderRadius: '16px', padding: '4px 10px', fontSize: '0.85em', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', gap: '4px'
                                }}>
                                📖 {display}
                            </button>
                        );
                    }
                    return <div key={i} style={{ fontSize: '0.8em', color: '#888' }}>{line.replace(/^-\s*/, '')}</div>;
                })}
            </div>
        );
    };

    const renderContent = (text: string) => {
        const { prose, citationBlock } = splitContent(text);
        return (
            <>
                <div>{renderProse(prose)}</div>
                {renderCitations(citationBlock)}
            </>
        );
    };

    const handleManualSpeak = (text: string) => {
        speakPhrase(localSanitizeForSpeech(text));
    };

    const handleStopAudio = () => {
        stopChat();
    };

    // Handle clearing chat
    const handleClearChat = () => {
        if (confirm("Reset chat history?")) {
            setMessages([]);
        }
    };

    return (
        <div className="chat-drawer" style={{}}>
            {/* Header: Title + Settings/Close */}
            <div style={{ padding: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--surface-color)', borderBottom: '1px solid var(--border-color)' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <h3 style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Ask WiKiBoox</h3>
                    {chatStats && (
                        <span style={{ fontSize: '0.7em', color: chatStats.dailyUsed > (chatStats.dailyLimit - 10) ? '#ff6b6b' : '#888' }}>
                            {chatStats.dailyLimit - chatStats.dailyUsed} chats left today
                        </span>
                    )}
                </div>
                <div>
                    <button onClick={handleClearChat} style={{ background: 'transparent', padding: '5px', fontSize: '0.8em', color: 'var(--text-secondary)' }} title="New Chat">🗑️</button>
                    <button onClick={onOpenSettings} style={{ background: 'transparent', padding: '5px' }}>⚙️</button>
                    <button onClick={onClose} style={{ background: 'transparent', padding: '5px', marginLeft: '5px' }}>✕</button>
                </div>
            </div>

            {/* Toolbar: Audio + Length + Stop */}
            <div style={{ padding: '8px 10px', background: 'var(--surface-hover)', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9em' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'var(--surface-color)', padding: '2px 6px', borderRadius: '4px' }}>
                    <span style={{ fontSize: '0.8em', color: 'var(--text-secondary)' }}>Audio</span>
                    <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '24px', height: '14px' }}>
                        <input type="checkbox" checked={audioEnabled} onChange={e => setAudioEnabled(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
                        <span style={{
                            position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: audioEnabled ? '#4CAF50' : '#555', borderRadius: '14px', transition: '.4s'
                        }}>
                            <span style={{
                                position: 'absolute', content: '""', height: '10px', width: '10px', left: '2px', bottom: '2px',
                                backgroundColor: 'white', transition: '.4s', borderRadius: '50%',
                                transform: audioEnabled ? 'translateX(10px)' : 'translateX(0)'
                            }} />
                        </span>
                    </label>
                </div>

                <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.8em', gap: '4px', cursor: 'pointer', background: 'var(--surface-color)', padding: '2px 6px', borderRadius: '4px' }}>
                    <input type="checkbox" checked={studyMode} onChange={onToggleStudyMode} />
                    <span>Study Mode</span>
                </label>

                <select
                    value={replyStyle}
                    onChange={e => setReplyStyle(e.target.value as any)}
                    style={{ background: 'var(--surface-color)', color: 'var(--text-color)', border: '1px solid var(--border-color)', padding: '3px', borderRadius: '4px', fontSize: '0.9em' }}
                >
                    <option value="concise">Concise</option>
                    <option value="normal">Normal</option>
                    <option value="detailed">Detailed</option>
                </select>

                {isPublicDomain && (
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.8em', gap: '4px', cursor: 'pointer', background: 'var(--surface-color)', padding: '2px 6px', borderRadius: '4px', border: authorVoice ? '1px solid var(--accent-color)' : '1px solid transparent' }}>
                        <input type="checkbox" checked={authorVoice} onChange={e => setAuthorVoice(e.target.checked)} style={{ display: 'none' }} />
                        <span style={{ opacity: authorVoice ? 1 : 0.7 }}>✒️ Author Voice</span>
                    </label>
                )}

                <button onClick={handleStopAudio} className="btn-danger" style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.9em', marginLeft: 'auto' }}>
                    Stop
                </button>
            </div>


            <div style={{ flex: 1, overflowY: 'auto', padding: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }} ref={scrollRef}>
                {messages.length === 0 && (
                    <div style={{ color: '#888', textAlign: 'center', marginTop: '20px' }}>
                        <h4 style={{ margin: '0 0 10px 0', color: 'var(--text-color)' }}>Ask about this book</h4>
                        <p style={{ margin: 0 }}>Search, summarize, or explain a passage. You can also ask for an image prompt inspired by what you’re reading.</p>
                    </div>
                )}
                {messages.map((m, i) => (
                    <div key={i} style={{
                        alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                        background: m.role === 'user' ? 'var(--chat-bubble-user)' : 'var(--chat-bubble-bot)',
                        color: m.role === 'user' ? 'var(--chat-bubble-user-text)' : 'var(--chat-bubble-bot-text)',
                        border: m.role === 'assistant' ? '1px solid var(--border-color)' : 'none',
                        padding: '8px 12px', borderRadius: '8px', maxWidth: '85%', lineHeight: '1.4',
                        position: 'relative'
                    }}>
                        {(m as any).isImagePlaceholder ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '10px' }}>
                                <div className="spinner" style={{
                                    border: '4px solid rgba(0, 0, 0, 0.1)',
                                    borderLeft: '4px solid var(--accent-color)',
                                    borderRadius: '50%',
                                    width: '24px',
                                    height: '24px',
                                    animation: 'spin 1s linear infinite'
                                }} />
                                <span style={{ fontSize: '0.9em', color: 'var(--text-secondary)' }}>Painting scene...</span>
                                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                            </div>
                        ) : (
                            <>
                                {m.imageUrl && (
                                    <div style={{ marginTop: '10px', cursor: 'pointer' }} onClick={() => setExpandedImage(m.imageUrl || null)} title="Click to expand">
                                        <img src={m.imageUrl} alt="AI Generation" style={{ width: '100%', borderRadius: '8px', border: '1px solid #444', transition: 'transform 0.2s' }} />
                                        <div style={{ textAlign: 'right', marginTop: '4px' }}>
                                            <button onClick={(e) => { e.stopPropagation(); executeImageGeneration(m.role === 'assistant' ? input : m.content); }} style={{ fontSize: '0.75em', padding: '4px 8px', background: 'var(--surface-color)', border: '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                                                ↻ Regenerate
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {renderContent(m.content)}
                                {m.role === 'assistant' && (
                                    <div style={{ marginTop: '5px', textAlign: 'right' }}>
                                        {m.content.startsWith('Image failed') && (
                                            <button onClick={() => handleGenerateImage()} style={{ marginRight: '8px', background: 'transparent', border: '1px solid #ff6b6b', color: '#ff6b6b', borderRadius: '4px', cursor: 'pointer', padding: '2px 6px', fontSize: '0.8em' }}>
                                                ↻ Retry Image
                                            </button>
                                        )}
                                        <button onClick={() => handleManualSpeak(m.content)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', opacity: 0.6, fontSize: '1.1em' }} title="Speak reply">🔊</button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                ))}
            </div>

            <div style={{ padding: '10px', borderTop: '1px solid var(--border-color)', background: 'var(--surface-color)' }}>
                {/* Context Pill */}
                {includeContext && (contextText || currentVerses.length > 0) && (
                    <div style={{ padding: '0 5px 8px 5px' }}>
                        <div style={{
                            fontSize: '0.75rem', background: 'rgba(33, 150, 243, 0.1)', color: '#2196F3',
                            padding: '4px 8px', borderRadius: '12px', display: 'inline-block', border: '1px solid rgba(33, 150, 243, 0.3)'
                        }} title="AI sees this page content">
                            Context: {bookTitle || currentRef} • {chapterTitle || currentRef.split(' ').slice(1).join(' ')} {pageLabel ? `• ${pageLabel}` : ''}
                        </div>
                    </div>
                )}

                {/* Validation Error Banner */}
                {validationError && (
                    <div style={{
                        color: 'var(--text-color)', background: '#d32f2f', padding: '4px 8px',
                        marginBottom: '8px', borderRadius: '4px', fontSize: '0.9em',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                        <span>{validationError}</span>
                        <button onClick={() => setValidationError(null)} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer' }}>✕</button>
                    </div>
                )}

                {/* ... Input Area ... */}
                <div style={{ marginBottom: '8px', fontSize: '0.8em', color: '#aaa', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input type="checkbox" checked={includeContext} onChange={e => setIncludeContext(e.target.checked)} style={{ marginRight: '5px' }} />
                        Explain this page
                    </label>
                    {/* @ts-ignore */}
                    {(localStorage.getItem('fc_tester_pin') || sessionStorage.getItem('fc_tester_pin')) && (
                        // Always show Generate Image (disabled if generating, or cancel if generating)
                        isGeneratingImage ? (
                            <button onClick={handleCancelImage} style={{ fontSize: '0.9em', color: '#ff6b6b', background: 'transparent', border: '1px solid #ff6b6b', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer' }}>
                                Cancel
                            </button>
                        ) : (
                            <button onClick={openImageModal} style={{ fontSize: '0.9em', color: 'var(--accent-color)', background: 'transparent', border: '1px solid var(--accent-color)', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer' }}>
                                🎨 Generate Image {usageStats ? `(${usageStats.dailyLimit - usageStats.dailyUsed} left)` : ''}
                            </button>
                        )
                    )}
                </div>
                <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                    {/* Mic Button */}
                    {SpeechRecognition && (
                        <button
                            onClick={toggleListening}
                            style={{
                                background: isListening ? 'var(--btn-danger-bg)' : 'var(--surface-hover)',
                                color: 'var(--text-color)',
                                border: '1px solid var(--border-color)', borderRadius: '50%', width: '36px', height: '36px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2em', cursor: 'pointer'
                            }}
                            title="Dictate"
                        >
                            {isListening ? '⏹' : '🎤'}
                        </button>
                    )}

                    <input
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSend()}
                        placeholder={isListening ? "Listening..." : "Ask a question..."}
                        style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--surface-hover)', color: 'var(--text-color)' }}
                    />
                    <button onClick={handleSend} disabled={isLoading} className="btn-primary" style={{ padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}>Send</button>
                </div>
            </div>

            {/* Fullscreen Image Modal - Overlay */}
            {expandedImage && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.92)', zIndex: 10000,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    padding: '20px'
                }} onClick={() => setExpandedImage(null)}>
                    {/* Top Right Controls */}
                    <div style={{
                        position: 'absolute', top: '20px', right: '20px',
                        display: 'flex', gap: '15px'
                    }}>
                        <a href={expandedImage} download={`crowley-img-${Date.now()}.png`}
                            onClick={e => e.stopPropagation()}
                            style={{
                                color: 'white', background: 'rgba(255,255,255,0.2)', padding: '8px 16px', borderRadius: '8px',
                                textDecoration: 'none', fontWeight: 'bold', border: '1px solid rgba(255,255,255,0.4)'
                            }}>
                            Download
                        </a>
                        <button onClick={() => setExpandedImage(null)} style={{
                            background: 'transparent', border: 'none', color: 'white', fontSize: '2em', cursor: 'pointer', lineHeight: 1
                        }}>✕</button>
                    </div>

                    <img src={expandedImage} alt="Expanded" style={{
                        maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', borderRadius: '8px',
                        boxShadow: '0 0 30px rgba(0,0,0,0.8)'
                    }} onClick={e => e.stopPropagation()} />
                </div>
            )}

            {/* Image Generation Modal */}
            {imageModalOpen && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)', zIndex: 10001,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }} onClick={() => setImageModalOpen(false)}>
                    <div style={{
                        background: 'var(--surface-color)', padding: '20px', borderRadius: '12px',
                        width: '90%', maxWidth: '400px', boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                        color: 'var(--text-color)'
                    }} onClick={e => e.stopPropagation()}>
                        <h3>Generate Image</h3>
                        {usageStats && (
                            <div style={{ marginBottom: '15px', fontSize: '0.9em', color: usageStats.dailyUsed >= usageStats.dailyLimit ? '#f88' : '#aaa' }}>
                                Daily Usage: <b>{usageStats.dailyUsed}/{usageStats.dailyLimit}</b> {usageStats.isPro ? '(Pro)' : '(Free)'}
                                {usageStats.dailyUsed >= usageStats.dailyLimit && <div style={{ marginTop: '5px' }}>Daily limit reached. Come back tomorrow!</div>}
                            </div>
                        )}

                        <textarea
                            value={imagePrompt}
                            onChange={e => setImagePrompt(e.target.value)}
                            placeholder="Describe the scene..."
                            style={{ width: '100%', height: '80px', padding: '8px', borderRadius: '6px', background: '#222', color: '#fff', border: '1px solid #444' }}
                        />

                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button onClick={handleCoach} disabled={isCoaching || (usageStats && usageStats.dailyUsed >= usageStats.dailyLimit)}
                                style={{ flex: 1, padding: '8px', background: '#673AB7', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', opacity: isCoaching ? 0.7 : 1 }}>
                                {isCoaching ? 'Optimizing...' : '✨ Optimize Prompt'}
                            </button>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                            <button onClick={() => setImageModalOpen(false)} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid #666', color: '#ccc', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
                            <button onClick={() => executeImageGeneration(imagePrompt)}
                                disabled={!imagePrompt.trim() || (usageStats && usageStats.dailyUsed >= usageStats.dailyLimit)}
                                style={{ padding: '8px 16px', background: 'var(--accent-color)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', opacity: (!imagePrompt.trim() || (usageStats && usageStats.dailyUsed >= usageStats.dailyLimit)) ? 0.5 : 1 }}>
                                Generate
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
